import { LocationOn } from "@mui/icons-material";
import { Box, Link, SxProps, Tooltip, Typography } from "@mui/material";
import { ReactNode, useContext } from "react";
import { RandomTextSkeleton } from "../../../../App/Components/Skeleton/RandomTextSkeleton";
import { CandidateContext } from "../../../CandidateProfile";
import { AppFeatureLookup, defaultUnauthorizedMessage } from "../../../../App/Constants/AbacConstants";
import { useFeaturePermission } from "../../../../App/State/Hooks/abacHooks";
import { ICandidateProfile } from "../../../../App/Typings/CandidateProfile";

export default function ProfilePhotoName({ sx }: { sx?: SxProps }) {
  const { candidate } = useContext(CandidateContext);

  return (
    <Box sx={sx}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      ></Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: { xs: "flex-start", md: "center" },
          gap: "0.5rem",
        }}
      >
        <Typography
          variant="h2"
          component="h2"
          data-test={"candidate-header-name"}
          sx={{
            fontSize: { xs: "1.2rem", sm: "1.5rem" },
            whiteSpace: "nowrap",
            marginTop: "0.25em",
            color: "secondary.main",
          }}
        >
          {candidate ? `${candidate.firstName} ${candidate.lastName}` : <RandomTextSkeleton />}
        </Typography>
        <Typography
          variant="h3"
          component="h3"
          data-test={"candidate-header-occupation"}
          sx={{
            fontFamily: "fontFamily",
            fontSize: { xs: "0.9rem", sm: "1rem" },
            whiteSpace: "nowrap",
            color: "primary.main",
          }}
        >
          {candidate ? candidate.title : <RandomTextSkeleton />}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 1,
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
        }}
      >
        <LocationLink candidate={candidate} />
      </Box>
    </Box>
  );
}

const LocationLink = ({ candidate }: { candidate: ICandidateProfile }) => {
  const locationLinkAccess = useFeaturePermission(AppFeatureLookup.ProfileCandidateLocationLink);
  const candidatePrimaryLocation = candidate?.locations?.[0];
  const isLinkableLocation =
    candidatePrimaryLocation !== undefined &&
    candidatePrimaryLocation.name !== "" &&
    candidatePrimaryLocation.coordinates !== null &&
    candidatePrimaryLocation.coordinates.lon !== 0 &&
    candidatePrimaryLocation.coordinates.lat !== 0;
  let candidateLocationDisplay: ReactNode = "";

  if (!locationLinkAccess) {
    candidateLocationDisplay = (
      <Tooltip title={defaultUnauthorizedMessage}>
        <Typography sx={{ fontWeight: "bold" }} data-test={"Location Not Available"}>
          Location Not Available
        </Typography>
      </Tooltip>
    );
  } else if (isLinkableLocation) {
    // https://developers.google.com/maps/documentation/urls/get-started
    candidateLocationDisplay = (
      <Link
        title="Open in Google Maps"
        data-test={candidatePrimaryLocation.name}
        target="_blank"
        rel="noreferrer"
        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          candidatePrimaryLocation.name,
        )}`}
      >
        {candidatePrimaryLocation.name}
      </Link>
    );
  } else {
    candidateLocationDisplay = (
      <Typography sx={{ fontWeight: "bold" }} data-test={candidatePrimaryLocation?.name || "Not Available"}>
        {candidatePrimaryLocation?.name || "Not Available"}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <LocationOn sx={{ marginRight: "0.5ch" }} />
      <Typography
        variant="h4"
        component="h4"
        data-test={"candidate-header-location"}
        sx={{
          fontFamily: "fontFamily",
          fontSize: "0.875rem",
          whiteSpace: "nowrap",
          display: "flex",
          alignItems: "center",
          color: "rgba(0,0,0,.6)",
        }}
      >
        {candidate ? candidateLocationDisplay : <RandomTextSkeleton />}
      </Typography>
    </Box>
  );
};
